/**
 * Fait : la traduction entre ce qu'une personne écrit — « 12,34 » — et l'unité
 *   du produit, le CENTIME ENTIER. Dans les deux sens, et sans jamais faire
 *   passer un montant par un nombre à virgule.
 *
 *   POURQUOI C'EST UN MODULE À PART, et non trois lignes dans l'écran :
 *   `Math.round(parseFloat('12,34'.replace(',', '.')) * 100)` a l'air juste et
 *   l'est presque toujours. Presque : `12.34 * 100` vaut
 *   1233.9999999999998 en virgule flottante, `0.29 * 100` vaut
 *   28.999999999999996, et l'arrondi qui rattrape se pose là où personne ne le
 *   relit. Ici la partie entière et les centimes sont deux ENTIERS, lus
 *   séparément dans le texte et recomposés par une multiplication exacte.
 *
 * Ne fait pas : il ne décide d'aucun plafond, n'appelle rien, n'affiche rien,
 *   et ne connaît aucune autre monnaie que l'euro — le produit n'en a qu'une.
 */

/**
 * Un montant écrit à la main : des chiffres, éventuellement un séparateur
 * décimal — la virgule française comme le point — et au plus deux décimales.
 * Pas de signe : un plafond négatif n'existe pas, et le refuser ici évite de
 * l'envoyer au serveur pour se faire dire « la demande est mal formée ».
 */
const MONTANT_ECRIT = /^(\d+)(?:[.,](\d{1,2}))?$/;

/**
 * Les espaces qu'un copier-coller traîne. Écrites en points de code, jamais en
 * clair : une espace insécable et une espace fine sont invisibles à la relecture,
 * et l'une des deux disparaîtrait un jour d'un reformatage sans que personne le
 * voie — le champ refuserait alors un montant collé depuis un tableur.
 */
const ESPACES = /[\s\u00a0\u202f]/g;

/**
 * Lit un montant en euros et rend des CENTIMES entiers, ou `null` si le texte
 * n'est pas un montant. `null` n'est pas zéro : zéro est un plafond qui ferme
 * tout, et confondre les deux poserait ce plafond-là sur une faute de frappe.
 */
export function enCentimes(saisie: string): number | null {
  /*
   * LE SYMBOLE EURO EST TOLÉRÉ, et il le faut : l'écran affiche « 12,34 € »,
   * et le geste le plus naturel est de recopier ce qu'on lit. Le refuser
   * rendait illisible la raison du refus — la personne voyait le bon montant
   * dans le champ et un message qui lui disait d'écrire un montant. Observé
   * lors de l'épreuve indépendante du 2026-08-25.
   */
  const trouve = MONTANT_ECRIT.exec(saisie.replace(/€/g, '').replace(ESPACES, ''));
  if (trouve === null) return null;

  const euros = Number.parseInt(trouve[1] ?? '0', 10);

  // « 12,3 » vaut trente centimes, pas trois : la décimale manquante est un
  // zéro À DROITE. Le padding se fait sur le TEXTE, avant tout calcul.
  const centimes = Number.parseInt((trouve[2] ?? '0').padEnd(2, '0'), 10);

  return euros * 100 + centimes;
}

/**
 * Affiche des centimes en euros lisibles. L'unité reste le centime : cette
 * fonction est le SEUL endroit où l'on divise par cent, et elle le fait sur
 * des entiers — quotient et reste — jamais en virgule flottante.
 *
 * La partie entière passe par le formateur français pour ses séparateurs de
 * milliers : « 1 000,00 € » se lit, « 100000 € » se compte sur les doigts.
 */
export function enEuros(centimes: number): string {
  const negatif = centimes < 0;
  const absolu = Math.abs(Math.trunc(centimes));

  const partieEntiere = new Intl.NumberFormat('fr-FR').format(Math.trunc(absolu / 100));
  const decimales = String(absolu % 100).padStart(2, '0');

  return `${negatif ? '−' : ''}${partieEntiere},${decimales} €`;
}

/**
 * L'inverse d'`enCentimes`, pour REMPLIR un champ de saisie avec un montant
 * existant. Sans séparateur de milliers ni symbole : ce qui remplit un champ
 * doit pouvoir en ressortir tel quel, et `enCentimes` refuse le « € ».
 */
export function enSaisie(centimes: number): string {
  const absolu = Math.abs(Math.trunc(centimes));

  return `${Math.trunc(absolu / 100)},${String(absolu % 100).padStart(2, '0')}`;
}
